/**
 * Decision Brief — Type Definitions
 *
 * These types mirror the database schema from migrations 504 + 505.
 * All date fields are ISO-8601 strings from Supabase.
 */

export type BriefStatus = 'draft' | 'collecting' | 'comparing' | 'decided' | 'deferred' | 'archived';

/** How a brief was resolved (migration 910). */
export type BriefResolutionType = 'accept' | 'accept_with_conditions' | 'defer' | 'reject_all';

export const BRIEF_RESOLUTION_LABELS: Record<BriefResolutionType, string> = {
  accept: 'Accepted',
  accept_with_conditions: 'Accepted with conditions',
  defer: 'Deferred',
  reject_all: 'All options rejected',
};

// Controlled vocabulary for decision classification (migration 505)
export type DecisionType =
  | 'strategic'       // Directional — where is the organization going
  | 'investment'      // Resource allocation — budget, headcount, capital
  | 'product'         // What to build or not build
  | 'operational'     // How we work — process, policy
  | 'technical'       // Architecture, standards, platform
  | 'organizational'  // Structure, roles, reporting lines, people
  | 'policy';         // Governance, compliance, legal posture

export type DecisionScope =
  | 'organization'  // Affects the whole company
  | 'division'      // Affects a business unit or division
  | 'department'    // Affects a function or department
  | 'team'          // Affects a specific team
  | 'project';      // Scoped to a single project or initiative

// How the brief's decision affects the Plan of Record (migration 507)
export type PromotionType = 'creates' | 'adds' | 'changes' | 'reverses';

export const PROMOTION_TYPE_LABELS: Record<PromotionType, string> = {
  creates:  'Creates a new commitment',
  adds:     'Adds to an existing plan entry',
  changes:  'Changes a prior commitment',
  reverses: 'Reverses or cancels a commitment',
};

export const PROMOTION_TYPE_DESCRIPTIONS: Record<PromotionType, string> = {
  creates:  'This decision establishes something net-new in the Plan of Record.',
  adds:     'This decision documents a choice against an existing planned initiative.',
  changes:  'This decision modifies, narrows, or expands a prior commitment — the prior decision is superseded.',
  reverses: 'This decision stops or cancels something already in the Plan of Record.',
};

export interface Brief {
  id: string;
  workspace_id: string;
  created_by: string;
  title: string;
  context: string | null;
  deadline: string | null;           // ISO date (YYYY-MM-DD)
  decision_authority: string | null;
  /** Who has a say (migration 794; was appended to context as "Voice holders: …"). */
  voice_holders: string | null;
  /** Who is kept informed but does not decide (migration 794). */
  observers: string | null;
  status: BriefStatus;
  view_token: string;
  // Classification fields (migration 505)
  decision_type: DecisionType | null;
  decision_scope: DecisionScope | null;
  scope_label: string | null;        // Free-text org unit, e.g. "Product Engineering"
  tags: string[];
  // Plan of Record linkage (migration 507)
  promoted_decision_id: string | null;  // ID of Decision record in main app
  promotion_type: PromotionType | null;
  promotion_note: string | null;        // What prior commitment is affected (for changes/reverses)
  promoted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BriefCriteria {
  id: string;
  brief_id: string;
  name: string;
  description: string | null;
  weight: number;                   // 1–5
  sort_order: number;
  created_at: string;
}

export interface BriefOption {
  id: string;
  brief_id: string;
  name: string;
  owner_name: string | null;
  owner_email: string | null;
  submit_token: string;
  sort_order: number;
  /** When the owner was first sent their private link (migration 794). */
  invited_at: string | null;
  /** When the owner was last reminded (migration 794). */
  last_reminded_at: string | null;
  created_at: string;
}

export interface BriefProposal {
  id: string;
  option_id: string;
  rationale: string | null;
  investment_summary: string | null;
  expected_outcome: string | null;
  assumptions: string[];
  opportunity_cost: string | null;
  top_risk: string | null;
  additional_notes: string | null;
  submitted_at: string;
  updated_at: string;
}

// ── Classification label and style maps ──────────────────────────────────────

export const DECISION_TYPE_LABELS: Record<DecisionType, string> = {
  strategic:      'Strategic',
  investment:     'Investment',
  product:        'Product',
  operational:    'Operational',
  technical:      'Technical',
  organizational: 'Organizational',
  policy:         'Policy',
};

export const DECISION_TYPE_COLORS: Record<DecisionType, { text: string; bg: string; border: string }> = {
  strategic:      { text: 'text-violet-400',  bg: 'bg-violet-400/10',  border: 'border-violet-400/30' },
  investment:     { text: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/30'  },
  product:        { text: 'text-sky-400',     bg: 'bg-sky-400/10',     border: 'border-sky-400/30'    },
  operational:    { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/30'},
  technical:      { text: 'text-cyan-400',    bg: 'bg-cyan-400/10',    border: 'border-cyan-400/30'   },
  organizational: { text: 'text-rose-400',    bg: 'bg-rose-400/10',    border: 'border-rose-400/30'   },
  policy:         { text: 'text-orange-400',  bg: 'bg-orange-400/10',  border: 'border-orange-400/30' },
};

export const DECISION_SCOPE_LABELS: Record<DecisionScope, string> = {
  organization: 'Company-wide',
  division:     'Division',
  department:   'Department',
  team:         'Team',
  project:      'Project',
};

export interface BriefScore {
  id: string;
  option_id: string;
  criteria_id: string;
  score: number;                    // 1–5
  note: string | null;
  scored_by: string;
  scored_at: string;
}

export interface BriefDecision {
  id: string;
  brief_id: string;
  selected_option_ids: string[];
  selected_rationale: string | null;
  rejected_rationale: string | null;
  conditions: string | null;
  /** Migration 910. Null only on rows read before 910 was applied. */
  resolution_type?: BriefResolutionType | null;
  /** What would make us look at this again (910). */
  signal_for_change?: string | null;
  /** For a deferral: when to look again (910). */
  revisit_when?: string | null;
  decided_by: string | null;
  decided_at: string;
}

// ── Enriched view types (joined from the public RPC) ─────────────────────────

export interface BriefOptionWithProposal extends BriefOption {
  proposal: BriefProposal | null;
  scores: BriefScore[];
}

export interface BriefPublicView {
  id: string;
  title: string;
  context: string | null;
  deadline: string | null;
  decision_authority: string | null;
  status: BriefStatus;
  // Classification fields (migration 505)
  decision_type: DecisionType | null;
  decision_scope: DecisionScope | null;
  scope_label: string | null;
  tags: string[];
  created_at: string;
  criteria: BriefCriteria[];
  options: BriefOptionWithProposal[];
  proposals: BriefProposal[];
  decision: BriefDecision | null;
}

// Alias — BriefView.tsx imported as ViewTokenView historically
export type ViewTokenView = BriefPublicView;

// ── Submission token view (from get_option_by_submit_token) ──────────────────

export interface SubmitTokenView {
  id: string;
  name: string;
  owner_name: string | null;
  brief_title: string;
  brief_context: string | null;
  deadline: string | null;
  status: BriefStatus;
  criteria: BriefCriteria[];
  existing_proposal: BriefProposal | null;
}

// ── Draft state (used in BriefBuilder wizard) ─────────────────────────────────

export interface BriefDraft {
  title: string;
  context: string;
  deadline: string;
  decision_authority: string;
  decision_type: DecisionType | '';
  decision_scope: DecisionScope | '';
  scope_label: string;
  tags: string;               // comma-separated in the form; split on save
  criteria: Array<{ name: string; description: string; weight: number }>;
  options: Array<{ name: string; owner_name: string; owner_email: string }>;
}

export const BRIEF_STATUS_LABELS: Record<BriefStatus, string> = {
  draft: 'Draft',
  collecting: 'Collecting',
  comparing: 'Comparing',
  decided: 'Decided',
  deferred: 'Deferred',
  archived: 'Archived',
};

export const BRIEF_STATUS_DESCRIPTION: Record<BriefStatus, string> = {
  draft: 'Being set up — criteria and options not yet sent to owners',
  collecting: 'Proposal submissions open — owners can submit via their link',
  comparing: 'Submissions closed — scoring and comparison in progress',
  decided: 'Decision recorded and brief locked',
  deferred: 'Put off until a named time or event; can be reopened',
  archived: 'Archived — not counted against plan limits',
};
